<template>
  <div class="billing-plan">
    <div v-if="!getSettingIsFetching && currentPlanStatusValid" class="panel-default current-plan">
      <div :class="{ 'current-sub': true, [getStatus]: true, 'without-addon': !getIsAiVisibilityEnabled }">
        <p class="title">{{ $t('current-subscription') }}</p>
        <p class="desc">{{ keywordsCount | keywordsFilter }} Keywords with {{ cycleUpdates | hourlyFilter }} updates</p>
        <div class="badge">
          {{ getBadgeText }}
        </div>
        <div class="plan-price">
          ${{ packagePrice }}/{{ packageBillingInterval }}
          <span class="plan-discount" v-if="totalDiscount !== 0">
            ({{ $t('billing-discount', { discount: totalDiscount }) }})
          </span>
        </div>
        <div class="plan-date-block">
          <p v-if="getStatus === 'success'">
            Renewal Date: <b>{{ getFormattedDate(tsInMilliseconds) }}</b>
          </p>
          <p v-else-if="getStatus === 'warning'">
            Trial ends: <b>{{ getFormattedDate(tsInMilliseconds) }}</b>
          </p>
          <p v-else-if="getStatus === 'error'">
            {{ getErrorText }}: <b>{{ getFormattedDate(tsInMilliseconds) }}</b>
          </p>
        </div>
        <kw-button :button-text="$t('billing-update-plan')" button-type="primary" @click="updateSerpPlan" />
        <p v-if="getIsSerpCancellable" @click="openCancelSubscriptionModal" class="cancel-btn">
          {{ $t('cancel-serp-subscription') }}
        </p>
      </div>
      <div v-if="getIsAiVisibilityEnabled" :class="{ 'addon-sub': true, [getAddonStatus]: true }">
        <p class="title">{{ $t('add-on-subscriptions') }}</p>
        <template v-if="getAiVisibilitySubscription">
          <p class="desc">
            AI Visibility Tracking with
            {{ credits }} credits
          </p>
          <div class="badge">{{ getAddonBadgeText }}</div>
          <div class="plan-price">${{ addonPrice }}/monthly</div>
          <div class="plan-date-block">
            <p v-if="getAddonStatus === 'success'">
              Plan Renewal Date: <b>{{ addonTsInMilliseconds }}</b>
            </p>
            <!-- <p v-else-if="getAddonStatus === 'warning'">warning</p> -->
            <p v-else>
              Plan ends on: <b>{{ addonTsInMilliseconds }}</b>
            </p>
          </div>
        </template>
        <template v-else>
          <p class="sub-title">{{ $t('ai-visibility-tracking') }}</p>
          <p class="info-text">{{ $t('unlock-ai-visibility-and-track-your-domains') }}</p>
        </template>

        <kw-button
          :button-text="getAddonButtonText"
          button-type="primary"
          @click="openAiVisibilityModal"
          :buttonClasses="'rankscale'"
        />
        <p v-if="getAddonStatus === 'success'" @click="openAddonCancel" class="cancel-btn">
          {{ $t('cancel-add-on-subscription') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import PlanError from './components/PlanError';
import PlanValid from './components/PlanValid';
import PlanWarning from './components/PlanWarning';
import SubscribeToAiVisibilityModal from '@/components/modals/SubscribeToAiVisibilityModal.vue';
import { beforeModalOpen, beforeModalClose } from '@/helpers/modalHelpers';
import { isAiPackage } from '~/views/auth/billing-utils';

export default {
  name: 'Billing',
  components: {
    'plan-error': PlanError,
    'plan-valid': PlanValid,
    'plan-warning': PlanWarning,
  },
  props: {
    openCancelSubscriptionModal: { type: Function },
    openAddonCancel: { type: Function },
  },
  filters: {
    keywordsFilter(keywords) {
      return keywords.toLocaleString();
    },
    hourlyFilter(value) {
      return value === 'hourly' ? 'bihourly' : value;
    },
  },
  computed: {
    ...mapGetters([
      'getInitialTrialPeriod',
      'getCurrentPlan',
      'getFormattedDate',
      'getSettingIsFetching',
      'getAiVisibilitySubscription',
      'getIsFreePlan',
      'getIsAiVisibilityEnabled',
      'getUserData',
      'getPackages',
    ]),
    keywordsCount() {
      return this.getCurrentPlan.subscription.package || '';
    },
    cycleUpdates() {
      return this.getCurrentPlan.subscription.cycle || '';
    },
    packagePrice() {
      return this.getCurrentPlan.subscription.stripe_plan_amount || 0;
    },
    totalDiscount() {
      return this.getCurrentPlan.subscription.total_discount || 0;
    },
    packageBillingInterval() {
      return this.getCurrentPlan.subscription.billing_cycle || '';
    },
    tsInMilliseconds() {
      return new Date(this.getCurrentPlan.subscription.account_expire_at).getTime();
    },
    addonTsInMilliseconds() {
      if (this.getAiVisibilitySubscription?.cancel_at) {
        return this.getFormattedDate(this.getAiVisibilitySubscription.cancel_at * 1000);
      }
      return this.getAiVisibilitySubscription
        ? this.getFormattedDate(this.getAiVisibilitySubscription.next_billing_at_ts * 1000)
        : '-';
    },
    addonPrice() {
      return this.getAiVisibilitySubscription.final_price ?? '-';
    },
    currentPlanStatusValid() {
      const defaultStatus = 'na';

      const { status = defaultStatus, subscription } = this.getCurrentPlan;

      if (subscription && (subscription.active === 2 || subscription.active === 0)) {
        return true;
      }

      // comming from legacy payment systems
      if (subscription && subscription.active === 1 && !subscription.scripe_active) {
        return true;
      }

      return status !== defaultStatus;
    },
    getStatus() {
      const { subscription, status } = this.getCurrentPlan;
      let planStatus = status;

      if (this.getIsTrial) {
        planStatus = 'warning';
      } else if (!subscription.isExpired && !subscription.cancel_at && planStatus !== 'error') {
        planStatus = 'success';
      }

      return planStatus;
    },
    getIsSerpCancellable() {
      const userData = this.getUserData?._original;
      return !!userData && !userData.subscription.is_free_plan && this.getStatus === 'success';
    },
    getAddonStatus() {
      if (this.getAiVisibilitySubscription?.cancel_at_period_end) {
        return 'error';
      }

      let planStatus = this.getAiVisibilitySubscription?.status;

      switch (planStatus) {
        case 'active':
          return 'success';
        default:
          return 'error';
      }
    },
    getPlanClasses() {
      return {
        plan: true,
        'plan-name-success': this.getCurrentPlan.status === 'success',
        'plan-name-waring': this.getCurrentPlan.status === 'warning',
        'plan-name-error': this.getCurrentPlan.status === 'error',
      };
    },
    getIsTrial() {
      return this.getCurrentPlan.subscription.active === 2;
    },
    credits() {
      return this.getAiVisibilitySubscription ? this.getAiVisibilitySubscription.credits.toLocaleString('en') : '-';
    },
    getAddonButtonText() {
      return this.getAiVisibilitySubscription ? this.$t('billing-update-addon') : this.$t('sign-up-for-ai-visibility');
    },
    getBadgeText() {
      if (this.getIsTrial) return 'TRIALING';
      switch (this.getStatus) {
        case 'error':
          return 'CANCELED';

        default:
          return 'ACTIVE';
      }
    },
    getAddonBadgeText() {
      if (this.getAddonStatus === 'error') return 'CANCELED';
      return this.getAiVisibilitySubscription?.status;
    },
    getErrorText() {
      return new Date().getTime() > this.tsInMilliseconds ? 'Plan cancelled' : 'Plan ends on';
    },
  },
  async mounted() {
    await this.fetchPackages();

    if (_.isEmpty(this.getCurrentPlan) && !this.getSettingIsFetching) {
      await this.loadPlan();
    }
  },
  methods: {
    ...mapActions(['fetchSettings', 'fetchPackages']),
    async loadPlan() {
      await this.fetchSettings();
      this.checkLoadedPlan();
    },

    checkLoadedPlan() {
      if (!this.currentPlanStatusValid) {
        this.$toastr.e(this.$t('billing-load-plan-error'));
      }
    },
    openAiVisibilityModal() {
      if (Math.random() >= 0 || Array.isArray(this.getUserData._original.default_card)) {
        const aiPackage = this.getPackages.find(isAiPackage);
        if (aiPackage) {
          this.$router.push({
            name: 'AccountDetails',
            query: { id: aiPackage.package_identity },
          });
          return;
        }
      }
      this.$modal.show(
        SubscribeToAiVisibilityModal,
        {
          update: !!this.getAiVisibilitySubscription,
        },
        {
          classes: 'v--modal v-mobile-custom-scroll',
          height: 'auto',
          width: document.body.offsetWidth < 649 ? '90%' : 640,
          name: 'SubscribeToAiVisibilityModal',
        },
        {
          'before-open': beforeModalOpen,
          'before-close': beforeModalClose,
        },
      );
    },
    // TODO add proper modal
    updateSerpPlan() {
      this.$router.push({
        name: 'AccountDetails',
        query: { id: this.getCurrentPlan.subscription.package_identity },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.selected-plan {
  margin: 10px 0;
}

.billing-plan {
  padding: 16px 24px;
  p.title {
    font-weight: 500;
    font-size: 16px;
    line-height: 18px;
    margin-bottom: 8px;
  }
  p.desc {
    font-weight: 500;
    font-size: 13px;
    line-height: 100%;
    color: #909399;
    margin-bottom: 8px;
  }
  .badge {
    padding: 2px 6px;
    border-radius: 4px;
    font-family: Roboto;
    font-weight: 500;
    font-size: 10px;
    line-height: 100%;
    letter-spacing: 0%;
    text-align: center;
    vertical-align: middle;
    margin-bottom: 8px;
    text-transform: uppercase;
    &.active {
      background-color: #078d0c;
    }
    &.canceled {
      background-color: #cb1a02;
    }
  }
  .plan-price {
    font-weight: 500;
    font-size: 13px;
    line-height: 100%;
    letter-spacing: 0%;
    color: #0074d9;
    margin-bottom: 8px;
  }
  .plan-date-block {
    font-weight: 400;
    font-size: 13px;
    line-height: 100%;
    letter-spacing: 0%;
    margin-bottom: 8px;
    b {
      font-weight: 500;
    }
  }
  .kw-button.kw-btn-primary {
    width: 100%;
  }
  .without-addon {
    .plan-date-block {
      margin-top: 10px;
    }
    p.desc {
      margin-top: 12px;
    }
    button.kw-btn-primary {
      margin-top: 42px;
    }
  }
}

.cancel-btn {
  cursor: pointer;
  font-weight: 500;
  font-size: 10px;
  line-height: 24px;
  letter-spacing: 0%;
  color: kw-color(kw-red, 1);
  margin: 8px 0 0;
}

.addon-sub {
  border-top: 1px solid #d7d9df;
  padding-top: 16px;
  margin-top: 16px;
  padding-bottom: 4px;
  .plan-price {
    color: #9136f9;
  }
  .sub-title {
    font-weight: 500;
    font-size: 13px;
    line-height: 100%;
    margin-bottom: 10px;
    color: kw-color(kw-grey, 1);
  }
  .info-text {
    font-weight: 400;
    font-style: italic;
    font-size: 11px;
    line-height: 100%;
    color: kw-color(kw-light-grey, 8);
    margin-bottom: 10px;
  }
}

.current-sub,
.addon-sub {
  &.success {
    .badge {
      background-color: #078d0c;
    }
  }
  &.warning {
    .badge {
      background-color: #fecf60;
    }
  }
  &.error {
    .badge {
      background-color: #cb1a02;
    }
  }
}
.upgrade-text-block {
  padding-top: 10px;
  font-size: 10px;
  line-height: 100%;
  letter-spacing: -0.2px;
  .head {
    color: kw-color(kw-purple, 11);
    font-weight: 600;
    line-height: 14px;
  }
  .link {
    color: kw-color(kw-purple, 11);
    cursor: pointer;
  }
}
</style>
